import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { CourseClient, CourseDTO } from '../services/apiClient';
import { API_BASE_URL, MAX_DROPDOWN_PAGE_SIZE } from '../config/config';
import axiosInstance from '../services/axiosInstance';

const apiClient = new CourseClient(API_BASE_URL, axiosInstance);

// Query hook - Get all courses for dropdowns (cached 4 hours)
export const useCourses = () => {
  return useQuery({
    queryKey: ['courses'],
    queryFn: async () => {
      const response = await apiClient.searchCourses({
        page: 1,
        pageSize: MAX_DROPDOWN_PAGE_SIZE,
        sortBy: 'Name',
      });
      // NSwag's ApiResponse_searchResponseDTO_1 codegen collision (see useStateApi.ts) —
      // runtime data is genuinely CourseDTO[].
      return (response?.data?.results as CourseDTO[] | undefined) || [];
    },
    staleTime: 4 * 60 * 60 * 1000, // 4 hours
    refetchOnReconnect: true,
  });
};

// Direct API search (no caching) - for admin tables with server-side paging
export const useCourseSearch = () => {
  const search = async (params: {
    page: number;
    pageSize: number;
    sortBy?: string;
    sortDescending?: boolean;
    filters?: any;
  }) => {
    const response = await apiClient.searchCourses({
      page: params.page,
      pageSize: params.pageSize,
      sortBy: params.sortBy || 'CreatedUtc',
      sortDescending: params.sortDescending ?? true,
      filters: params.filters || {},
    });
    return {
      results: (response?.data?.results as CourseDTO[] | undefined) || [],
      totalCount: response?.data?.totalCount || 0,
    };
  };

  return { search };
};

// Query hook - Get single course by ID
export const useCourseById = (id: string) => {
  return useQuery({
    queryKey: ['courses', id],
    queryFn: async () => {
      const response = await apiClient.getCourseById(id);
      return response?.data;
    },
    enabled: !!id,
    staleTime: Infinity,
    refetchOnReconnect: true,
  });
};

// Mutation hook - Create course
export const useCreateCourse = () => {
  const queryClient = useQueryClient();

  return useMutation({
    // Returns the saved course: creating one from a course request needs its id to approve it.
    mutationFn: async (data: CourseDTO) => {
      const response = await apiClient.createCourse(data);
      return response?.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['courses'] });
    },
  });
};

// Mutation hook - Update course
export const useUpdateCourse = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: CourseDTO }) => {
      await apiClient.updateCourse(id, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['courses'] });
    },
  });
};

// Mutation hook - Delete course
export const useDeleteCourse = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.deleteCourse(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['courses'] });
    },
  });
};
